import React from 'react';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { IItemCreationSubsectionInputProps, ItemCreationSubsection } from '~/Components';
import { useTranslation } from 'react-i18next';
import { ItemCreationValidInput } from '~/Components/ItemCreation/ItemCreationSubsection/ItemCreationSubsectionInput.model';
import { ExtendedLocation } from '../ExtendedLocation.model';
import { AtSign, GroupSport, MapIcon, ProductCardsColorful, Merchant } from '~/Components/Icons';
import { makeStyles } from '~/UIKit/Style';
import { Web } from '~/Components/Icons/Web';
import {
  LocationEditCustomGoogleMap,
  LocationEditCustomInputAddress,
} from './LocationEditCustomElements';
import { LIGHTER_BLUE, WHITE } from '~/Constants/Colors.constants';
import { PhoneSimple } from '~/Components/Icons/PhoneSimple';
import { useCountries } from '../useCountries';

import { useNavigation, useHasAccess, AccessList } from '~/Hooks';
import { IMerchant } from '~/Models/DTO/Merchant.models';
import { useMerchant } from '~/ScreensOrganisation/OrganisationMerchant/HelperMerchant/useMerchant';

const useStyles = makeStyles((theme) => ({
  container: {
    paddingBottom: 30,
  },
  itemContainer: {
    padding: 0,
    backgroundColor: WHITE,
    [theme.breakpoints.up('md')]: {
      backgroundColor: LIGHTER_BLUE,
      padding: theme.spacing(2),
    },
  },
}));
export const LocationEditGeneralSection = (location: ExtendedLocation) => {
  const { t } = useTranslation();
  const { getSearchParams } = useNavigation();
  const { id } = getSearchParams();
  const { classes } = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { countries, isAddress } = useCountries(location);
  const { merchantList } = useMerchant();
  const { hasAccess } = useHasAccess();

  const LocationGeneralDetails: IItemCreationSubsectionInputProps<Partial<ExtendedLocation>>[] = [
    ...[
      id && {
        label: `${t('merchantForm.title')}`,
        placeholder: t('merchantForm.title'),
        type: ItemCreationValidInput.SELECT,
        name: 'merchant',
        noIconBackground: true,
        icon: <Merchant />,
        disabled: !hasAccess(AccessList.GLOBAL_WARNING),
        options: merchantList?.map((merchant: IMerchant) => ({
          value: merchant.uuid,
          label: `${merchant.name} (${merchant.merchantId})`,
        })),
        hide: !merchantList?.length,
      },
    ],
    {
      icon: <GroupSport />,
      label: `${t('form.field.name')}*`,
      placeholder: t('form.field.placeholder.name'),
      type: ItemCreationValidInput.TEXT,
      name: 'name',
      hasAnError: location?.name?.length === 0,
      InputProps: { inputProps: { maxLength: 50 } },
      noIconBackground: true,
      tooltip: { title: t('form.field.location.name.hint'), placement: 'bottom-start' },
    },
    {
      customElement: <LocationEditCustomInputAddress countries={countries} />,
    },
  ];

  const LocationGeneralContacts: IItemCreationSubsectionInputProps<Partial<ExtendedLocation>>[] = [
    {
      icon: <AtSign />,
      label: t('app.customer.contactinfo.email'),
      placeholder: t('form.field.placeholder.email'),
      type: ItemCreationValidInput.TEXT,
      name: 'email',
    },
    {
      icon: <PhoneSimple />,
      label: t('form.field.placeholder.phone'),
      placeholder: t('form.field.placeholder.phone'),
      type: ItemCreationValidInput.TEXT,
      name: 'phone',
    },
    {
      icon: <Web />,
      label: t('app.website'),
      placeholder: t('app.website'),
      type: ItemCreationValidInput.TEXT,
      name: 'website',
    },
  ];

  const LocationGeneralLocation: IItemCreationSubsectionInputProps<Partial<ExtendedLocation>>[] = [
    {
      label: `${t('form.field.location.street')}*`,
      placeholder: t('form.field.location.street'),
      type: ItemCreationValidInput.TEXT,
      name: 'street',
      hidden: !isAddress,
      icon: <MapIcon />,
    },
    {
      label: `${t('form.field.location.zip')}*`,
      placeholder: t('form.field.location.zip'),
      type: ItemCreationValidInput.TEXT,
      name: 'zipCode',
      hidden: !isAddress,
      icon: <MapIcon />,
    },
    {
      label: `${t('form.field.location.city')}*`,
      placeholder: t('form.field.location.city'),
      type: ItemCreationValidInput.TEXT,
      name: 'city',
      hidden: !isAddress,
      icon: <MapIcon />,
    },
    {
      label: `${t('form.field.location.country')}*`,
      type: ItemCreationValidInput.SELECT,
      name: 'countryISOA2',
      options: countries,
      hidden: !isAddress,
      icon: <MapIcon />,
    },
  ];

  const LocationGeneralLocationGoogleMap: IItemCreationSubsectionInputProps<
    Partial<ExtendedLocation>
  >[] = [
    {
      customElement: <LocationEditCustomGoogleMap />,
    },
  ];

  return (
    <div className={classes.container}>
      <ItemCreationSubsection items={LocationGeneralDetails} />
      <ItemCreationSubsection
        title={t('form.field.location.editLocationDescription')}
        isHeaderVisible={Boolean(isAddress)}
        items={LocationGeneralLocation}
        itemClasses={{ inputContainer: Boolean(isAddress) ? classes.itemContainer : '' }}
      />
      <ItemCreationSubsection
        title={t('form.field.location.editLocationGoogleMapDescription')}
        isHeaderVisible={Boolean(isAddress)}
        items={LocationGeneralLocationGoogleMap}
      />
      <ItemCreationSubsection
        title={t('form.title.locationContacts')}
        isHeaderVisible
        items={LocationGeneralContacts}
        tooltip={{
          title: t('form.field.locationContacts.hint'),
          icon: <ProductCardsColorful />,
          placement: isMobile ? 'auto' : 'auto-start',
        }}
      />
    </div>
  );
};
